import React, { useState, useEffect } from 'react';
import customerService from '../../db/customer-service';
import LocationButton from '../LocationButton/LocationButton';
import './UserProfile.scss';

const UserProfile = ({ isOpen, onClose }) => {
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [originalProfile, setOriginalProfile] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [addresses, setAddresses] = useState([]);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressForm, setAddressForm] = useState({
    fullName: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pincode: '',
    isDefault: false
  });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [locationMessage, setLocationMessage] = useState('');
  const [locationSuccess, setLocationSuccess] = useState(false);
  const customerId = sessionStorage.getItem('customerId');
  const customerEmail = sessionStorage.getItem('customerEmail');

  useEffect(() => {
    if (isOpen && customerId) {
      loadProfile();
      loadAddresses();
    }
  }, [isOpen, customerId]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const customer = await customerService.getCustomerById(customerId);
      const profileData = {
        name: customer.name || (customer.fname + ' ' + customer.lname).trim() || '',
        email: customer.email || '',
        phone: customer.phone || customer.mobile || ''
      };
      setProfile(profileData);
      setOriginalProfile(profileData);
    } catch (error) {
      const profileData = {
        name: sessionStorage.getItem('customerName') || '',
        email: sessionStorage.getItem('customerEmail') || '',
        phone: sessionStorage.getItem('customerPhone') || ''
      };
      setProfile(profileData);
      setOriginalProfile(profileData);
    }
    setLoading(false);
  };

  const loadAddresses = async () => {
    try {
      const customerAddresses = await customerService.getCustomerAddresses(customerId);
      setAddresses(customerAddresses);
    } catch (error) {
      console.error('Error loading addresses:', error);
    }
  };

  const hasChanges = () => {
    return profile.name !== originalProfile.name;
  };

  const handleCancel = () => {
    setProfile(originalProfile);
    setEditing(false);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      let customer;
      try {
        customer = await customerService.getCustomerById(customerId);
      } catch (error) {
        customer = {
          _id: customerId,
          type: 'customer',
          createdAt: new Date().toISOString()
        };
      }

      customer.name = profile.name;
      customer.updatedAt = new Date().toISOString();

      await customerService.createCustomer(customer);
      
      sessionStorage.setItem('customerName', profile.name);
      
      setEditing(false);
      setOriginalProfile(profile);
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error updating profile. Please try again.');
    }
    setLoading(false);
  };

  const handleChange = (field, value) => {
    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddressFormChange = (field, value) => {
    setAddressForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetAddressForm = () => {
    setAddressForm({
      fullName: '',
      phone: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      pincode: '',
      isDefault: false
    });
    setEditingAddress(null);
    setShowAddressForm(false);
    setLocationMessage('');
    setLocationSuccess(false);
  };

  const handleLocationDetected = (address, message) => {
    // Auto-fill address fields
    setAddressForm(prev => ({
      ...prev,
      addressLine1: address.addressLine1 || '',
      addressLine2: address.addressLine2 || '',
      city: address.city || '',
      state: address.state || '',
      pincode: address.pincode || ''
    }));
    
    // Show success message
    setLocationMessage(message || 'Location detected successfully!');
    setLocationSuccess(true);
    
    // Clear success message after 5 seconds
    setTimeout(() => {
      setLocationMessage('');
      setLocationSuccess(false);
    }, 5000);
  };

  const handleLocationError = (errorMessage) => {
    setLocationMessage('');
    setLocationSuccess(false);
    // Error is handled by LocationButton component
  };

  const handleAddAddress = () => {
    resetAddressForm();
    setShowAddressForm(true);
  };

  const handleEditAddress = (address) => {
    setAddressForm({
      fullName: address.fullName || '',
      phone: address.phone || '',
      addressLine1: address.addressLine1 || '',
      addressLine2: address.addressLine2 || '',
      city: address.city || '',
      state: address.state || '',
      pincode: address.pincode || '',
      isDefault: address.isDefault || false
    });
    setEditingAddress(address.id);
    setShowAddressForm(true);
  };

  const handleSaveAddress = async () => {
    if (!addressForm.fullName || !addressForm.addressLine1 || !addressForm.city || !addressForm.state || !addressForm.pincode) {
      alert('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      if (editingAddress) {
        await customerService.updateCustomerAddress(customerId, editingAddress, addressForm);
      } else {
        await customerService.addCustomerAddress(customerId, addressForm);
      }
      
      await loadAddresses();
      resetAddressForm();
      alert('Address saved successfully!');
    } catch (error) {
      console.error('Error saving address:', error);
      alert('Error saving address. Please try again.');
    }
    setLoading(false);
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }

    setLoading(true);
    try {
      await customerService.deleteCustomerAddress(customerId, addressId);
      await loadAddresses();
      alert('Address deleted successfully!');
    } catch (error) {
      console.error('Error deleting address:', error);
      alert('Error deleting address. Please try again.');
    }
    setLoading(false);
  };

  const handleSetDefaultAddress = async (addressId) => {
    setLoading(true);
    try {
      await customerService.setDefaultAddress(customerId, addressId);
      await loadAddresses();
    } catch (error) {
      console.error('Error setting default address:', error);
      alert('Error setting default address. Please try again.');
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="profile-overlay" onClick={onClose}>
      <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
        <div className="profile-header">
          <h2>My Profile</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="profile-content">
          {loading ? (
            <div className="loading">Loading...</div>
          ) : (
            <>
              {/* Basic Profile Section */}
              <div className="profile-section">
                <h3>Basic Information</h3>

                <div className="profile-field">
                  <label>Name</label>
                  <input
                    type="text"
                    value={profile.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    disabled={!editing}
                  />
                </div>

                <div className="profile-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={profile.email}
                    disabled
                  />
                </div>

                <div className="profile-field">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={profile.phone}
                    disabled
                  />
                </div>

                <div className="profile-actions">
                  {editing ? (
                    <>
                      <button className="save-btn" onClick={handleSave} disabled={loading || !hasChanges()}>
                        {loading ? 'Saving...' : 'Save'}
                      </button>
                      <button className="cancel-btn" onClick={handleCancel}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button className="edit-btn" onClick={() => setEditing(true)}>
                      Edit Profile
                    </button>
                  )}
                </div>
              </div>

              {/* Addresses Section */}
              <div className="addresses-section">
                <div className="addresses-header">
                  <h3>My Addresses</h3>
                  <button className="add-address-btn" onClick={handleAddAddress}>
                    Add New Address
                  </button>
                </div>

                {addresses.length > 0 ? (
                  <div className="addresses-list">
                    {addresses.map(address => (
                      <div key={address.id} className={`address-card ${address.isDefault ? 'default' : ''}`}>
                        <div className="address-content">
                          <div className="address-header">
                            <h4>{address.fullName}</h4>
                            {address.isDefault && <span className="default-badge">Default</span>}
                          </div>
                          <p>{address.addressLine1}</p>
                          {address.addressLine2 && <p>{address.addressLine2}</p>}
                          <p>{address.city}, {address.state} - {address.pincode}</p>
                          <p>Phone: {address.phone}</p>
                        </div>
                        <div className="address-actions">
                          <button onClick={() => handleEditAddress(address)}>Edit</button>
                          {!address.isDefault && (
                            <button onClick={() => handleSetDefaultAddress(address.id)}>Set Default</button>
                          )}
                          <button onClick={() => handleDeleteAddress(address.id)} className="delete-btn">
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-addresses">
                    <p>No addresses saved yet.</p>
                  </div>
                )}

                {/* Address Form Modal */}
                {showAddressForm && (
                  <div className="address-form-modal">
                    <div className="address-form">
                      <h4>{editingAddress ? 'Edit Address' : 'Add New Address'}</h4>
                      
                      {/* Location Button */}
                      <LocationButton 
                        onLocationDetected={handleLocationDetected}
                        onError={handleLocationError}
                        className="form-integration compact"
                      />
                      
                      {/* Location Success Message */}
                      {locationSuccess && locationMessage && (
                        <div className="location-success-message">
                          <small>{locationMessage}</small>
                        </div>
                      )}
                      
                      <div className="form-row">
                        <input
                          type="text"
                          placeholder="Full Name *"
                          value={addressForm.fullName}
                          onChange={(e) => handleAddressFormChange('fullName', e.target.value)}
                        />
                        <input
                          type="tel"
                          placeholder="Phone Number *"
                          value={addressForm.phone}
                          onChange={(e) => handleAddressFormChange('phone', e.target.value)}
                        />
                      </div>
                      
                      <input
                        type="text"
                        placeholder="Address Line 1 *"
                        value={addressForm.addressLine1}
                        onChange={(e) => handleAddressFormChange('addressLine1', e.target.value)}
                      />
                      
                      <input
                        type="text"
                        placeholder="Address Line 2 (Optional)"
                        value={addressForm.addressLine2}
                        onChange={(e) => handleAddressFormChange('addressLine2', e.target.value)}
                      />
                      
                      <div className="form-row">
                        <input
                          type="text"
                          placeholder="City *"
                          value={addressForm.city}
                          onChange={(e) => handleAddressFormChange('city', e.target.value)}
                        />
                        <input
                          type="text"
                          placeholder="State *"
                          value={addressForm.state}
                          onChange={(e) => handleAddressFormChange('state', e.target.value)}
                        />
                      </div>
                      
                      <input
                        type="text"
                        placeholder="Pincode *"
                        value={addressForm.pincode}
                        onChange={(e) => handleAddressFormChange('pincode', e.target.value)}
                        maxLength="6"
                      />
                      
                      <div className="checkbox-container">
                        <label>
                          <input
                            type="checkbox"
                            checked={addressForm.isDefault}
                            onChange={(e) => handleAddressFormChange('isDefault', e.target.checked)}
                          />
                          Set as default address
                        </label>
                      </div>
                      
                      <div className="form-actions">
                        <button onClick={handleSaveAddress} disabled={loading}>
                          {loading ? 'Saving...' : 'Save Address'}
                        </button>
                        <button onClick={resetAddressForm}>Cancel</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;